/**
 * @fileoverview ProvisionDevices Component
 * 
 * This component manages the device provisioning workflow for smart home devices.
 * It handles the step-by-step process of pairing devices with sensors, including:
 * - Device discovery and pairing
 * - Sensor mapping and configuration
 * - SmartStart integration
 * - Manual linking/unlinking
 * - Autolinking functionality
 * - Device reset and removal
 * 
 * Key Features:
 * - Multi-step provisioning workflow with progress tracking
 * - Real-time device polling and state synchronization
 * - Automatic sensor mapping (autolinking)
 * - Support for both manual and SmartStart pairing modes
 * - Comprehensive error handling and user feedback
 * - Integration with property manifest and device APIs
 * 
 * State Management:
 * - Uses ProvisioningContext for shared state across child components
 * - Manages device pairing/unpairing states
 * - Tracks autolinking progress and sensor mappings
 * - Handles modal states for various provisioning actions
 * 
 */

import { useAppSelector } from '@app/hooks';
import { RootState } from '@app/store';
import ExpandableSnack from '@features/Common/ExpandableSnack';
import {
  PropertyManifestEntry,
  SensorMap,
} from '@features/home-manifest/types';
import { Device } from '@features/home/types';
import { ManifestDevice, Sensor } from '@features/plan-manifest/types';
import { ApiErrorResponse, SnackType } from '@lib/types';
import { getErrorMessage } from '@lib/utils';
import { Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useBeginExclusionModeMutation,
  useBeginInclusionModeMutation,
  useConfigureDeviceMutation,
  useUpdatePropertyManifestEntryMutation,
  useBackupZwaveNvramMutation,
} from '../../api';
import DeviceEventLog from './DeviceEventLog';
import DeviceModal from './DeviceModal';
import DevicesToPair from './DevicesToPair';
import NoManifestEntriesMessage from './NoManifestEntriesMessage';
import PairingConfirmation from './pairing-confirmation/PairingConfirmation';
import PairingModal from './PairingModal';
import ProvisionStepper from './ProvisionStepper';
import ResetModal from './ResetModal';
import UnpairingModal from './UnpairingModal';
import { ProvisioningContext, ProvisioningContextType } from './ProvisioningContext';

import {
  useGetDevicesQuery,
  useGetPropertyManifestQuery,
} from '@features/home/api';
import { pollingInterval } from '@features/tenant-selector/types';
import { Category } from '@lib/labels';
import ManualLinkModal from './ManualLinkModal';
import ManualUnlinkModal from './ManualUnlinkModal';
import SmartstartDialog from './SmartstartDialog';
import { debounce } from '@features/charts/utils';

/**
 * Main provisioning component that orchestrates the device pairing workflow
 * 
 * This component manages the entire device provisioning process including:
 * - Step-by-step device pairing workflow
 * - Real-time device discovery and state tracking
 * - Automatic sensor mapping (autolinking)
 * - Manual pairing and SmartStart integration
 * - Error handling and user feedback
 * 
 * The component uses a multi-step stepper interface where each step represents
 * a group of devices that need to be provisioned in a specific order.
 */
const ProvisionDevices = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Redux state selectors
  const userTenant = useAppSelector((state: RootState) => state.userTenant);
  const selectedTenant = useAppSelector((state: RootState) => state.tenant);
  const property = useAppSelector((state: RootState) => state.property);

  // Derived state from Redux
  const devices = useMemo(() => property.devices || [], [property.devices]);
  const currentTenantId = useMemo(
    () => (selectedTenant.currentTenant || userTenant)?.tenant_id,
    [selectedTenant.currentTenant, userTenant]
  );
  const manifestEntries = useMemo(
    () => property.manifestEntries,
    [property.manifestEntries]
  );

  // Local state for UI management
  const [activeStep, setActiveStep] = useState(0);
  const [pairing, setPairing] = useState<boolean>(false);
  const [unpairing, setUnpairing] = useState<boolean>(false);
  
  // Modal state management
  const [deviceToLink, setDeviceToLink] = useState<
    [PropertyManifestEntry, Sensor] | null
  >(null);
  const [deviceToUnpair, setDeviceToRemove] =
    useState<PropertyManifestEntry | null>(null);
  const [deviceToPair, setDeviceToPair] =
    useState<PropertyManifestEntry | null>(null);
  const [deviceToScan, setDeviceToScan] =
    useState<PropertyManifestEntry | null>(null);
  const [deviceToReset, setDeviceToReset] =
    useState<PropertyManifestEntry | null>(null);
  const [deviceToManualLink, setDeviceToManualLink] =
    useState<PropertyManifestEntry | null>(null);
  const [deviceToManualUnlink, setDeviceToManualUnlink] =
    useState<PropertyManifestEntry | null>(null);

  // API query hooks for data fetching
  const { refetch: refetchDevices } = useGetDevicesQuery(
    {
      tenantId: currentTenantId || '',
      propertyId: property?.property_id || '',
    },
    {
      skip: !currentTenantId || !property?.property_id,
    }
  );

  const { refetch: refetchManifest } = useGetPropertyManifestQuery(
    {
      tenantId: currentTenantId || '',
      propertyId: property?.property_id || '',
      includeEntries: true,
    },
    {
      skip: !currentTenantId || !property?.property_id,
    }
  );

  // API mutation hooks
  const [beginInlusionMode] = useBeginInclusionModeMutation();
  const [beginExclusionMode] = useBeginExclusionModeMutation();
  const [configureDevice] = useConfigureDeviceMutation();
  const [updatePropertyManifestEntry] = useUpdatePropertyManifestEntryMutation();
  const [backupZwaveNvram, { isLoading: isBackingUp }] = useBackupZwaveNvramMutation();

  /**
   * Navigation handlers for the stepper component
   */
  const handleNext = () => {
    setActiveStep((prevActiveStep: number) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep: number) => prevActiveStep - 1);
  };

  /**
   * Formats a manifest entry with sensor mapping data
   * 
   * This function updates a manifest entry with new sensor-to-entity mappings
   * and sets the provisioned timestamp when all sensors are paired.
   * 
   * @param entityId - The entity ID to map to the sensor
   * @param entry - The manifest entry to update
   * @param sensor - The sensor being paired
   * @returns Partial manifest entry with updated sensor_map and provisioned timestamp
   */
  const formatEntry = useCallback(
    (
      entityId: string,
      entry: PropertyManifestEntry,
      sensor: Sensor
    ): Partial<PropertyManifestEntry> => {
      if (!manifestEntries) return {};

      const sensorPaired = (ss: Sensor) => {
        return (
          ss === sensor ||
          Boolean(entry.sensor_map?.[ss.sensor_id ?? ss.friendly_name])
        );
      };

      const provisioned = (entry.device as ManifestDevice).sensors.every(
        sensorPaired
      )
        ? Math.round(Date.now() / 1000).toString()
        : undefined;

      return {
        provisioned: provisioned ?? entry.provisioned,
        sensor_map: {
          ...(entry.sensor_map || {}),
          [sensor.sensor_id ?? sensor.friendly_name]: entityId,
        },
      };
    },
    [manifestEntries]
  );

  /**
   * Groups manifest entries by provision order to create workflow steps
   * 
   * This creates a map where each key represents a step number and the value
   * is a set of manifest entries that should be provisioned at that step.
   * System devices are filtered out as they don't require manual provisioning.
   */
  const devicesByStep = useMemo(() => {
    return manifestEntries
      .filter(
        (e) =>
          !(e.device as ManifestDevice)?.sensors?.some(
            (s) => s.sensor_category === Category.system
          )
      )
      .reduce((acc, entry) => {
        const order = Number(entry.provision_order);
        const devicesAtStep = acc.get(order) || new Set();
        devicesAtStep.add(entry);
        acc.set(order, devicesAtStep);
        return acc;
      }, new Map<number, Set<PropertyManifestEntry>>());
  }, [manifestEntries]);

  // Derived state for workflow management
  const steps = useMemo(
    () => (devicesByStep ? Array.from(devicesByStep.values()) : []),
    [devicesByStep]
  );
  const totalSteps = useMemo(() => steps.length, [steps.length]);
  const currentManifestDevices = useMemo(
    () => Array.from((steps[activeStep] || new Set()).values()),
    [activeStep, steps]
  );

  /**
   * Polling effects for real-time data synchronization
   * 
   * These effects ensure that device and manifest data stays current
   * during the provisioning process. Manifest data is polled more frequently
   * than device data to provide responsive feedback.
   */
  useEffect(() => {
    const interval = setInterval(() => {
      refetchManifest();
    }, pollingInterval / 4);
    return () => clearInterval(interval);
  }, [refetchManifest]);

  useEffect(() => {
    const interval = setInterval(() => {
      refetchDevices();
    }, pollingInterval);
    return () => clearInterval(interval);
  }, [refetchDevices]);

  /**
   * Maps device IDs to their associated entity IDs for tracking
   * 
   * This creates a lookup table that maps each device ID to a set of
   * entity IDs that belong to that device. Used for device discovery
   * and autolinking functionality.
   */
  const entitiesByDevice: Record<string, Set<string>> | null = useMemo(() => {
    return (
      devices?.reduce<Record<string, Set<string>>>(
        (a, v) => ({
          ...a,
          [v.data.device_id]: new Set([
            ...Array.from((a[v.data.device_id] || new Set()).values()),
            v.data.entity_id,
          ]),
        }),
        {}
      ) || null
    );
  }, [devices]);

  const [prevEntitiesByDevice, setPrevEntitiesByDevice] = useState<
    typeof entitiesByDevice | null
  >(null);

  // Autolinking state management
  const isAutolinkMappingInitialized = useRef<boolean>(false);
  const remainingSensorsToAutolink = useRef<Sensor[]>([]);
  const autolinkSensorMap = useRef<SensorMap | null>(null);
  const pairedDeviceId = useRef<string | undefined>();
  const manifestEntryRef = useRef<PropertyManifestEntry | null>(null);
  
  /**
   * Debounced manifest update function for autolinking
   * 
   * This function updates the manifest entry with autolinked sensor mappings
   * and gateway device ID. It's debounced to prevent excessive API calls
   * during rapid autolinking updates.
   */
  const debouncedUpdate = useRef(
    debounce(() => {
      updatePropertyManifestEntry({
        userTenantId: currentTenantId || '',
        propertyId: property?.property_id || '',
        entryId: manifestEntryRef?.current?.manifest_entry_id || '',
        body: {
          gateway_device_id: pairedDeviceId.current,
          sensor_map: autolinkSensorMap.current!,
        },
      }).catch((err) => {
        console.warn('Failed to update manifest:', err);
      });
    }, 30000)
  ).current;

  /**
   * Cleans up autolinking state when moving between steps
   */
  const autolinkCleanup = useCallback(() => {
    isAutolinkMappingInitialized.current = false;
    remainingSensorsToAutolink.current = [];
    autolinkSensorMap.current = null;
    pairedDeviceId.current = undefined;
  }, []);

  useEffect(() => {
    autolinkCleanup();
  }, [activeStep, autolinkCleanup]);

  /**
   * Finds the entity ID for a sensor based on its pairing_id pattern
   * 
   * This function matches sensor pairing_id patterns against entity IDs
   * from the paired device. It uses regex patterns to find matching entities.
   * 
   * @param sensor - The sensor to find an entity ID for
   * @returns The matching entity ID or undefined if not found
   */
  const findEntityIdForSensor = useCallback(
    (sensor: Sensor) => {
      if (!pairedDeviceId.current || !sensor.pairing_id) return undefined;

      const entities = entitiesByDevice?.[pairedDeviceId.current] || [];
      const [prefix, suffix] = sensor.pairing_id.split('$');

      return Array.from(entities).find((id) => {
        const toMatch = id.replace(/_\d+$/, '');
        return toMatch.startsWith(prefix) && toMatch.endsWith(suffix);
      });
    },
    [entitiesByDevice]
  );

  /**
   * Executes the autolinking process for remaining sensors
   * 
   * This function iterates through remaining sensors and attempts to
   * find matching entity IDs. When matches are found, they are added
   * to the sensor map and removed from the remaining sensors list.
   */
  const runAutolink = useCallback(() => {
    const entry = steps[activeStep]
      ? Array.from(steps[activeStep])?.[0]
      : undefined;
    if (!entry) return;

    const newMapping = { ...autolinkSensorMap.current };
    const completedIndices: number[] = [];

    remainingSensorsToAutolink.current.forEach((s, i) => {
      const entityId = findEntityIdForSensor(s);
      if (entityId) {
        newMapping[s.friendly_name] = entityId;
        completedIndices.push(i);
      }
    });

    // Remove completed sensors from remaining list (reverse to maintain indices)
    completedIndices.reverse().forEach((i) => {
      remainingSensorsToAutolink.current.splice(i, 1);
    });

    autolinkSensorMap.current = newMapping;
  }, [activeStep, findEntityIdForSensor, steps]);

  /**
   * Main autolinking effect that manages the entire autolinking process
   * 
   * This effect handles:
   * - Initialization of autolinking when new devices are detected
   * - Running the autolinking algorithm when device entities change
   * - Updating the manifest when autolinking progress is made
   * - Cleaning up state when moving between steps
   */
  useEffect(() => {
    if (!entitiesByDevice || !prevEntitiesByDevice) return;

    const manifestEntry = steps[activeStep]
      ? Array.from(steps[activeStep])?.[0]
      : undefined;

    manifestEntryRef.current = manifestEntry ?? null;

    // Initialize autolinking when new devices are detected
    if (!isAutolinkMappingInitialized.current) {
      const currentDeviceIds = Object.keys(entitiesByDevice);
      const prevDeviceIds = Object.keys(prevEntitiesByDevice);
      const newDevices = [...currentDeviceIds].filter(
        (id) => !prevDeviceIds.includes(id)
      );

      pairedDeviceId.current = newDevices[0];

      if (!manifestEntry) return;

      const sensorProfiles =
        (manifestEntry.device as ManifestDevice).sensors || [];
      remainingSensorsToAutolink.current = [...sensorProfiles];

      if (manifestEntry.sensor_map && pairedDeviceId.current) {
        autolinkSensorMap.current = { ...manifestEntry.sensor_map };
        isAutolinkMappingInitialized.current = true;
      }
    }

    // Run autolinking if there are remaining sensors
    if (
      remainingSensorsToAutolink.current.length > 0 &&
      pairedDeviceId.current
    ) {
      runAutolink();
    }

    // Update manifest if autolinking has made progress
    if (
      manifestEntry &&
      autolinkSensorMap.current &&
      JSON.stringify(autolinkSensorMap.current) !==
        JSON.stringify(manifestEntry.sensor_map)
    ) {
      debouncedUpdate();
    }
  }, [
    activeStep,
    debouncedUpdate,
    entitiesByDevice,
    prevEntitiesByDevice,
    property.property_id,
    runAutolink,
    steps,
    updatePropertyManifestEntry,
  ]);

  /**
   * Effect for tracking device and entity changes
   * 
   * This effect monitors changes in the device and entity lists and:
   * - Notifies users of new/removed devices and entities
   * - Automatically configures newly paired devices
   * - Updates gateway_device_id when devices are paired
   * - Handles state cleanup when devices are removed
   */
  useEffect(() => {
    if (entitiesByDevice !== null) {
      if (prevEntitiesByDevice !== null) {
        /* Notify user if any devices/entities were added or removed */
        const oldDevices = new Set(Object.keys(prevEntitiesByDevice));
        const newDevices = new Set(Object.keys(entitiesByDevice));
        const oldEntities = new Set(
          Object.values(prevEntitiesByDevice)
            .map((es) => Array.from(es.values()))
            .flat(1)
        );
        const newEntities = new Set(
          Object.values(entitiesByDevice)
            .map((es) => Array.from(es.values()))
            .flat(1)
        );

        newDevices.forEach((deviceId) => {
          if (deviceId && !oldDevices.has(deviceId)) {
            enqueueSnackbar(`New device added: ${deviceId}`, {
              variant: 'success',
            });
            setPairing(false);

            /* If a device was added during pairing, set the gateway_device_id on the open entry and configure the device */
            if (
              deviceToPair &&
              currentManifestDevices?.length === 1 &&
              !currentManifestDevices[0]?.gateway_device_id
            ) {
              const deviceName = (
                currentManifestDevices[0].device as ManifestDevice
              ).friendly_name;

              updatePropertyManifestEntry({
                userTenantId: currentTenantId || '',
                propertyId: property?.property_id || '',
                entryId: currentManifestDevices[0].manifest_entry_id,
                body: {
                  gateway_device_id: deviceId,
                },
              }).then(() => {
                enqueueSnackbar(`Paired device ${deviceId} to ${deviceName}`, {
                  variant: 'success',
                });
                refetchManifest();
              });
              configureDevice({
                userTenantId: currentTenantId || '',
                propertyId: property?.property_id || '',
                entryId: currentManifestDevices[0].manifest_entry_id,
              }).then(() => {
                enqueueSnackbar(`Configured device ${deviceName}`, {
                  variant: 'success',
                });
              });
            }
          }
        });

        newEntities.forEach((entityId) => {
          if (entityId && !oldEntities.has(entityId)) {
            enqueueSnackbar(`New device entity added: ${entityId}`, {
              variant: 'success',
            });
          }
        });

        oldDevices.forEach((deviceId) => {
          if (deviceId && !entitiesByDevice[deviceId]) {
            enqueueSnackbar(`Device removed: ${deviceId}`, {
              variant: 'warning',
            });
            setUnpairing(false);
            refetchManifest();
          }
        });

        oldEntities.forEach((entityId) => {
          if (entityId && !newEntities.has(entityId)) {
            enqueueSnackbar(`Device entity removed: ${entityId}`, {
              variant: 'warning',
            });
          }
        });
      }

      setPrevEntitiesByDevice(entitiesByDevice);
    }
  }, [
    configureDevice,
    currentManifestDevices,
    currentTenantId,
    deviceToPair,
    enqueueSnackbar,
    entitiesByDevice,
    prevEntitiesByDevice,
    property?.property_id,
    refetchManifest,
    updatePropertyManifestEntry,
  ]);

  /**
   * Handles pairing a device with a sensor
   * 
   * This function updates the manifest entry with the new sensor mapping
   * and sets the provisioned timestamp if all sensors are now paired.
   * 
   * @param dd - The device data containing the entity ID to pair
   */
  const handlePairing = useCallback(
    async (dd: Device) => {
      if (!manifestEntries || !deviceToLink) return;

      const updatedEntry = await updatePropertyManifestEntry({
        userTenantId: currentTenantId || '',
        propertyId: property?.property_id || '',
        entryId: deviceToLink[0].manifest_entry_id,
        body: formatEntry(dd.data.entity_id, deviceToLink[0], deviceToLink[1]),
      });

      const errorDetails = (updatedEntry as ApiErrorResponse)?.error;

      if (errorDetails) {
        enqueueSnackbar("Couldn't pair entry:", {
          key: 'entry-error',
          content: (
            <ExpandableSnack
              id="entry-error"
              message={"Couldn't pair entry:"}
              variant={SnackType.error}
              detail={getErrorMessage(errorDetails)}
            />
          ),
        });
      } else {
        enqueueSnackbar('Paired entry', {
          variant: 'success',
        });

        refetchDevices();
        refetchManifest();

        setDeviceToLink(null);
      }
    },
    [
      deviceToLink,
      enqueueSnackbar,
      formatEntry,
      manifestEntries,
      property?.property_id,
      refetchDevices,
      refetchManifest,
      updatePropertyManifestEntry,
      currentTenantId,
    ]
  );

  /**
   * Handles unpairing a sensor from a device
   * 
   * This function clears the sensor mapping from the manifest entry
   * and resets the provisioned timestamp if any sensors become unpaired.
   * 
   * @param manifestEntry - The manifest entry containing the sensor
   * @param sensor - The sensor to unpair
   */
  const handleUnpairing = useCallback(
    async (
      manifestEntry: PropertyManifestEntry,
      sensor: Sensor
    ): Promise<void> => {
      if (!currentTenantId) return;
      if (!property?.property_id) return;

      const updatedEntry = await updatePropertyManifestEntry({
        userTenantId: currentTenantId,
        propertyId: property.property_id,
        entryId: manifestEntry.manifest_entry_id,
        body: formatEntry('', manifestEntry, sensor),
      });

      const errorDetails = (updatedEntry as ApiErrorResponse)?.error;

      if (errorDetails) {
        enqueueSnackbar("Couldn't unpair entry:", {
          key: 'entry-error',
          content: (
            <ExpandableSnack
              id="entry-error"
              message={"Couldn't unpair entry:"}
              variant={SnackType.error}
              detail={getErrorMessage(errorDetails)}
            />
          ),
        });
      } else {
        enqueueSnackbar('Unpaired entry', {
          variant: 'success',
        });

        refetchDevices();
        refetchManifest();

        setDeviceToLink(null);
        autolinkCleanup();
      }

      refetchManifest();
      refetchDevices();
    },
    [
      autolinkCleanup,
      currentTenantId,
      enqueueSnackbar,
      formatEntry,
      property.property_id,
      refetchDevices,
      refetchManifest,
      updatePropertyManifestEntry,
    ]
  );

  /**
   * Begins pairing mode (inclusion mode) for device discovery
   * 
   * This function starts the Z-Wave inclusion mode which allows new devices
   * to be discovered and added to the network. The mode runs for 30 seconds
   * and automatically stops after the timeout.
   */
  const beginPairing = useCallback(async () => {
    const INCLUSION_MODE_DURATION = 30000 as const;

    if (!currentTenantId || !property?.property_id) return;
    if (pairing) return;
    const inclusionMode = await beginInlusionMode({
      userTenantId: currentTenantId,
      propertyId: property.property_id,
    });

    const errorDetails = (inclusionMode as ApiErrorResponse)?.error;

    if (errorDetails) {
      enqueueSnackbar("Couldn't start inclusion mode:", {
        key: 'inclusion-error',
        content: (
          <ExpandableSnack
            id="inclusion-error"
            message={"Couldn't start inclusion mode:"}
            variant={SnackType.error}
            detail={getErrorMessage(errorDetails)}
          />
        ),
      });
    } else {
      enqueueSnackbar('Started inclusion mode', {
        variant: 'success',
      });

      refetchDevices();
      refetchManifest();
    }

    setPairing(true);

    await new Promise((res) => {
      setTimeout(res, INCLUSION_MODE_DURATION);
    });

    setPairing(false);
  }, [
    beginInlusionMode,
    enqueueSnackbar,
    pairing,
    property.property_id,
    refetchDevices,
    refetchManifest,
    currentTenantId,
  ]);

  /**
   * Begins unpairing mode (exclusion mode) for device removal
   * 
   * This function starts the Z-Wave exclusion mode which allows devices
   * to be removed from the network. The mode runs for 30 seconds and
   * automatically stops after the timeout.
   */
  const beginUnpairing = useCallback(async () => {
    const EXCLUSION_MODE_DURATION = 30000 as const;

    if (!currentTenantId || !property?.property_id) return;
    if (unpairing) return;

    const exclusionMode = await beginExclusionMode({
      userTenantId: currentTenantId,
      propertyId: property.property_id,
    });

    const errorDetails = (exclusionMode as ApiErrorResponse)?.error;

    if (errorDetails) {
      enqueueSnackbar("Couldn't start exclusion mode:", {
        key: 'inclusion-error',
        content: (
          <ExpandableSnack
            id="exclusion-error"
            message={"Couldn't start exclusion mode:"}
            variant={SnackType.error}
            detail={getErrorMessage(errorDetails)}
          />
        ),
      });
    } else {
      enqueueSnackbar('Started exclusion mode', {
        variant: 'success',
      });

      refetchDevices();
      refetchManifest();
    }

    setUnpairing(true);

    await new Promise((res) => {
      setTimeout(res, EXCLUSION_MODE_DURATION);
    });

    setUnpairing(false);
  }, [
    beginExclusionMode,
    enqueueSnackbar,
    property.property_id,
    refetchDevices,
    refetchManifest,
    unpairing,
    currentTenantId,
  ]);

  /**
   * Triggers Z-Wave NVRAM backup for the property
   *
   * This function initiates a backup of the Z-Wave network NVRAM data,
   * which includes the network configuration and paired device information.
   * The backup is stored in the cloud and can be restored during provisioning.
   */
  const handleBackupZwave = useCallback(async () => {
    if (!currentTenantId || !property?.property_id) return;

    try {
      await backupZwaveNvram({
        userTenantId: currentTenantId,
        propertyId: property.property_id,
      }).unwrap();

      enqueueSnackbar('Z-Wave NVRAM backup completed successfully', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar("Couldn't backup Z-Wave NVRAM", {
        key: 'backup-error',
        content: (
          <ExpandableSnack
            id="backup-error"
            message={"Couldn't backup Z-Wave NVRAM:"}
            variant={SnackType.error}
            detail={getErrorMessage(error as ApiErrorResponse)}
          />
        ),
      });
    }
  }, [
    backupZwaveNvram,
    enqueueSnackbar,
    property?.property_id,
    currentTenantId,
  ]);

  /**
   * Context value for sharing state and handlers with child components
   */
  const contextValue: ProvisioningContextType = useMemo(() => ({
    devices: devices || [],
    entries: currentManifestDevices,
    onPairSensor: setDeviceToLink,
    onUnpairSensor: handleUnpairing,
    onRemoveDevice: setDeviceToRemove,
    onAddDevice: setDeviceToPair,
    onAddSmartstartDevice: setDeviceToScan,
    onResetDevice: setDeviceToReset,
    onManualUnlinkDevice: setDeviceToManualUnlink,
    onManualLinkDevice: setDeviceToManualLink,
  }), [
    devices,
    currentManifestDevices,
    handleUnpairing,
  ]);

  /* Display Confirmation Screen */
  const renderedConfirmationScreen = useMemo(
    () => (
      <PairingConfirmation
        manifestEntries={manifestEntries}
        onBackupZwave={handleBackupZwave}
        isBackingUp={isBackingUp}
      />
    ),
    [manifestEntries, handleBackupZwave, isBackingUp]
  );

  /* Displays progress spinner if manifest or devices are not ready*/
  if (!manifestEntries?.length || !devices) {
    return (
      <NoManifestEntriesMessage loading={!devices || !manifestEntries} />
    );
  }

  /* MAIN RENDER */
  return (
    <ProvisioningContext.Provider value={contextValue}>
      <Stack direction="row" sx={{ width: '100%', padding: 0.5, gap: 2 }}>
        <Stack alignItems="center" sx={{ flex: 1 }}>
          {activeStep === steps.length ? (
            renderedConfirmationScreen
          ) : (
            <DevicesToPair />
          )}
          <ProvisionStepper
            activeStep={activeStep}
            totalSteps={totalSteps}
            handleNext={handleNext}
            handleBack={handleBack}
          />
        </Stack>
        <DeviceEventLog />
      </Stack>
      {deviceToUnpair && (
        <UnpairingModal
          entry={
            manifestEntries.find(
              (e) => e.manifest_entry_id === deviceToUnpair.manifest_entry_id
            ) ?? deviceToUnpair
          }
          isUnpairing={unpairing}
          onUnpair={beginUnpairing}
          isOpen={!!deviceToUnpair}
          onClose={() => setDeviceToRemove(null)}
        />
      )}
      {deviceToPair && (
        <PairingModal
          entry={
            manifestEntries.find(
              (e) => e.manifest_entry_id === deviceToPair.manifest_entry_id
            ) ?? deviceToPair
          }
          isPairing={pairing}
          onPair={beginPairing}
          isOpen={!!deviceToPair}
          onClose={() => setDeviceToPair(null)}
        />
      )}
      {deviceToScan && (
        <SmartstartDialog
          entry={
            manifestEntries.find(
              (e) => e.manifest_entry_id === deviceToScan.manifest_entry_id
            ) ?? deviceToScan
          }
          isOpen={!!deviceToScan}
          onClose={() => setDeviceToScan(null)}
        />
      )}
      {deviceToReset && (
        <ResetModal
          entry={deviceToReset}
          isOpen={!!deviceToReset}
          onClose={() => setDeviceToReset(null)}
        />
      )}
      {deviceToManualLink && (
        <ManualLinkModal
          entry={deviceToManualLink}
          isOpen={!!deviceToManualLink}
          onClose={() => setDeviceToManualLink(null)}
        />
      )}
      {deviceToManualUnlink && (
        <ManualUnlinkModal
          device={deviceToManualUnlink}
          onSubmit={() => setDeviceToManualUnlink(null)}
        />
      )}
      {deviceToLink && (
        <DeviceModal
          isOpen={!!deviceToLink}
          entryArr={deviceToLink ? [deviceToLink[0]] : []}
          onClose={() => setDeviceToLink(null)}
          onSelect={handlePairing}
          sensorType={deviceToLink ? deviceToLink[1].sensor_category : null}
        />
      )}
    </ProvisioningContext.Provider>
  );
};

export default ProvisionDevices;
